import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  buildEmptyForm,
  buildPraticaPayload,
  normalizePraticaToForm,
  PfPraticaFormLayout
} from './pfPraticaFormShared';

export default function PfPraticaEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pratica, setPratica] = useState(null);
  const [lookups, setLookups] = useState({
    clienti: [],
    tipiOperazione: [],
    stati: [],
    istituti: []
  });
  const [formData, setFormData] = useState(buildEmptyForm());

  const defaultStatoId = useMemo(() => {
    const firstNonFinale = lookups.stati.find((row) => !Number(row.finale));
    return firstNonFinale?.id || lookups.stati[0]?.id || '';
  }, [lookups.stati]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [praticaRes, tipiRes, statiRes] = await Promise.all([
          api.get(`/pf-pratiche/${id}`),
          api.get('/pf-tipi-operazione'),
          api.get('/pf-stati-pratica')
        ]);

        const nextPratica = praticaRes.data || null;
        const currentClienteOption =
          nextPratica?.cliente_id
            ? {
                id: Number(nextPratica.cliente_id),
                label: nextPratica.cliente_label || `Cliente #${nextPratica.cliente_id}`,
                codice_fiscale: nextPratica.cliente_codice_fiscale || null,
                telefono: nextPratica.cliente_telefono || null,
                email: nextPratica.cliente_email || null,
                abilitato: 1
              }
            : null;
        const currentIstitutoOption =
          nextPratica?.istituto_erogante_id
            ? {
                id: Number(nextPratica.istituto_erogante_id),
                nome: nextPratica.istituto_erogante_nome || `Istituto #${nextPratica.istituto_erogante_id}`,
                codice: nextPratica.istituto_erogante_codice || null,
                tipo: nextPratica.istituto_erogante_tipo || null,
                referente: nextPratica.istituto_erogante_referente || null,
                attivo: 1
              }
            : null;
        const nextLookups = {
          clienti: currentClienteOption ? [currentClienteOption] : [],
          tipiOperazione: Array.isArray(tipiRes.data) ? tipiRes.data : [],
          stati: Array.isArray(statiRes.data) ? statiRes.data : [],
          istituti: currentIstitutoOption ? [currentIstitutoOption] : []
        };

        setPratica(nextPratica);
        setLookups(nextLookups);
        setFormData(normalizePraticaToForm(nextPratica, nextLookups.stati[0]?.id || ''));
        setError('');
      } catch (err) {
        console.error('Error fetching pf pratica edit data:', err);
        const message = err?.response?.status === 404
          ? 'Pratica non trovata'
          : 'Errore nel caricamento della pratica';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.cliente_id || !formData.tipo_operazione_id || !formData.stato_id) {
      toast.error('Cliente, tipo operazione e stato sono obbligatori');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/pf-pratiche/${id}`, buildPraticaPayload(formData));
      toast.success('Pratica aggiornata con successo');
      navigate(`/pratiche-finanziarie/pratiche/${id}`);
    } catch (err) {
      console.error('Error updating pf pratica:', err);
      const message = err?.response?.data?.message || 'Errore nel salvataggio della pratica';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PfPraticaFormLayout
      title={pratica ? `Modifica pratica #${pratica.id}` : 'Modifica pratica'}
      subtitle="Aggiorna dati base, workflow e parametri finanziari della pratica"
      submitLabel="Salva modifiche"
      saving={saving}
      error={error}
      formData={formData}
      lookups={lookups}
      onFormChange={handleFormChange}
      onSubmit={handleSubmit}
      onCancel={() => navigate(pratica ? `/pratiche-finanziarie/pratiche/${pratica.id}` : '/pratiche-finanziarie/pratiche')}
      onReset={() => setFormData(normalizePraticaToForm(pratica, defaultStatoId))}
    />
  );
}
